import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Drawer, Button, Badge, Typography, Input, Spin, Grid } from 'antd'
import { CustomerServiceOutlined, CloseOutlined } from '@ant-design/icons'
import {
  openSupportChat,
  closeSupportChat,
  enqueueSupportChatMessage,
  initializeSupportChat,
  sendSupportChatMessageThunk,
  loadSupportChatHistory,
  createMessage,
  markWelcomeSent,
} from '../../store/slices/supportChatSlice'
import './SupportChat.css'

const { Text } = Typography
const { TextArea } = Input
const { useBreakpoint } = Grid

const ChatBotIcon = () => (
  <svg
    className="support-chat-launcher__icon"
    viewBox="0 0 32 32"
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient
        id="support-chat-launcher-gradient"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
      >
        <stop offset="0%" stopColor="#8b5cf6" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
    </defs>
    <circle
      cx="16"
      cy="16"
      r="15"
      fill="url(#support-chat-launcher-gradient)"
    />
    <path
      d="M10 12h12c1.105 0 2 .895 2 2v5c0 1.105-.895 2-2 2h-2.2l-2.3 2.3a1 1 0 0 1-1.5 0L13.7 21H10c-1.105 0-2-.895-2-2v-5c0-1.105.895-2 2-2Z"
      fill="#fff"
      opacity="0.95"
    />
    <circle cx="13.25" cy="15.5" r="0.9" fill="#8b5cf6" />
    <circle cx="16" cy="15.5" r="0.9" fill="#8b5cf6" />
    <circle cx="18.75" cy="15.5" r="0.9" fill="#8b5cf6" />
  </svg>
)

const SendArrowIcon = () => (
  <svg
    className="support-chat-send-btn__icon"
    viewBox="0 0 28 28"
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4.3 11.6 23 4.4c1.1-.4 2.1.7 1.7 1.8l-5.7 16c-.4 1.2-2 1.4-2.6.3l-3.4-5.3-5.9-1c-1.3-.2-1.5-1.9-.3-2.3Z"
      fill="currentColor"
      opacity="0.95"
    />
    <path
      d="m6.1 11.9 9.4 3.2"
      stroke="#f6f5ff"
      strokeWidth="1.6"
      strokeLinecap="round"
      opacity="0.55"
    />
    <path
      d="m12.9 17.5 3.9 4.9"
      stroke="#f6f5ff"
      strokeWidth="1.6"
      strokeLinecap="round"
      opacity="0.55"
    />
    <path
      d="M15 15.4 21 7.8"
      stroke="#f2ebff"
      strokeWidth="1.6"
      strokeLinecap="round"
      opacity="0.7"
    />
  </svg>
)

const SupportChat = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    isOpen,
    messages,
    status,
    sending,
    sessionId,
    unreadCount,
    welcomeSent,
    historyLoaded,
  } = useSelector((state) => state.supportChat)
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef(null)
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const sessionReady = Boolean(sessionId)
  const isComposerDisabled = !sessionReady || status === 'connecting'

  const handleToggle = useCallback(() => {
    if (isOpen) {
      dispatch(closeSupportChat())
    } else {
      dispatch(openSupportChat())
    }
  }, [dispatch, isOpen])

  const handleClose = () => {
    dispatch(closeSupportChat())
  }

  const handleInputChange = (event) => {
    setInputValue(event.target.value)
  }

  const handleActionClick = useCallback((action) => {
    if (!action) {
      return
    }
    const { url } = action
    dispatch(closeSupportChat())
    if (url) {
      if (url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        navigate(url)
      }
      return
    }
    if (action.type === 'CONTACT_SUPPORT') {
      window.location.href = 'mailto:support@quantumhusky.ai'
    }
  }, [dispatch, navigate])

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (isOpen && !historyLoaded && status !== 'connecting' && status !== 'loading') {
      dispatch(loadSupportChatHistory(50))
    }
  }, [dispatch, isOpen, historyLoaded, status])

  useEffect(() => {
    if (isOpen && historyLoaded && !sessionId && status !== 'connecting') {
      dispatch(initializeSupportChat({ entryPoint: 'support_chat_launcher' }))
    }
  }, [dispatch, isOpen, historyLoaded, sessionId, status])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isOpen])

  useEffect(() => {
    if (isOpen && sessionId && !welcomeSent && messages.length === 0) {
      dispatch(enqueueSupportChatMessage(
        createMessage(
          'assistant',
          'Hi, I am Gamma, the QuantumHusky support assistant. What may I help?',
        ),
      ))
      dispatch(markWelcomeSent())
    }
  }, [dispatch, isOpen, sessionId, welcomeSent, messages.length])

  const handleSend = () => {
    const trimmed = inputValue.trim()
    if (!trimmed || sending || !sessionReady) {
      return
    }

    dispatch(enqueueSupportChatMessage(
      createMessage('user', trimmed),
    ))
    setInputValue('')

    dispatch(sendSupportChatMessageThunk({ content: trimmed }))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const messageItems = useMemo(() => {
    return messages.map((msg) => {
      const timestamp = msg.createdAt ? new Date(msg.createdAt) : new Date()
      const content = msg.content || ''

      return (
        <div
          key={msg.id}
          className={`support-chat-message support-chat-message--${msg.role === 'user' ? 'user' : 'assistant'}`}
        >
          {content && (
            <>
              <div className="support-chat-message__bubble" style={{ whiteSpace: 'pre-wrap' }}>
                {content}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {msg.role === 'user' ? 'You' : 'Gamma'} · {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </>
          )}
          {msg.role !== 'user'
            && Array.isArray(msg.metadata?.actions)
            && msg.metadata.actions.length > 0 && (
            <div className="support-chat-actions">
              {msg.metadata.actions.map((action, actionIdx) => (
                <Button
                  key={`${msg.id}-action-${actionIdx}-${action.label || action.url || action.type}`}
                  size="small"
                  onClick={() => handleActionClick(action)}
                >
                  {action.label || 'Open link'}
                </Button>
              ))}
            </div>
          )}
        </div>
      )
    })
  }, [messages, handleActionClick])

  return (
    <>
      {!isOpen && (
        <div className="support-chat-launcher">
          <Badge count={unreadCount} size="small" showZero={false}>
            <Button
              aria-label="Open AI support chat"
              className="support-chat-launcher__button"
              onClick={handleToggle}
            >
              <div className="support-chat-launcher__inner">
                <ChatBotIcon />
              </div>
            </Button>
          </Badge>
        </div>
      )}

      <Drawer
        open={isOpen}
        onClose={handleClose}
        placement={isMobile ? 'bottom' : 'right'}
        height={isMobile ? '85%' : undefined}
        width={isMobile ? '100%' : 380}
        className="support-chat-drawer"
        maskStyle={{ background: 'rgba(15, 23, 42, 0.4)' }}
        title={null}
        closable={false}
      >
        <div className="support-chat-header">
          <div className="support-chat-header__title">
            <CustomerServiceOutlined style={{ fontSize: 20, color: '#6366f1' }} />
            <div>
              <Text strong>Gamma</Text>
              <div style={{ fontSize: 12, color: sessionReady ? '#22c55e' : '#f97316' }}>
                {sessionReady ? 'Online' : 'Connecting...'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="support-chat-header__close"
            aria-label="Close AI support chat"
            onClick={handleClose}
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="support-chat-messages">
          {(status === 'connecting' || status === 'loading') && messages.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spin tip={status === 'loading' ? 'Loading chat history...' : 'Connecting...'} />
            </div>
          )}
          {messageItems}
          {sending && (
            <div className="support-chat-typing">
              <Spin size="small" />
              <span>Assistant is typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="support-chat-input">
          <div className="support-chat-composer">
            <TextArea
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question or describe the issue..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              disabled={isComposerDisabled}
            />
            <Button
              type="text"
              aria-label="Send message"
              onClick={handleSend}
              loading={sending}
              disabled={!inputValue.trim() || isComposerDisabled}
              className="support-chat-send-btn"
            >
              {!sending && <SendArrowIcon />}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default SupportChat
